import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { styled, css } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import type { FieldSubscription } from '@/js/libs/final-form';

import notify from 'src/modules/Notification';
import useErrorSubscription from './useErrorSubscription';

export const Wrapper = styled.span<{ $inField?: boolean }>`
  display: inline-block;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.critical100};
  ${({ $inField }) =>
    $inField &&
    css`
      position: absolute;
      bottom: 0;
      left: 15px;
      font-size: 10px;
    `}
`;

export interface ErrorProps<Name extends string> {
  name: Name;
  formatMessage?: (message: string) => string;
  subscription?: FieldSubscription;
  withTouched?: boolean;
  notifyOnly?: boolean;
  inField?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const Error: <Name extends string = string>(props: ErrorProps<Name>) => React.ReactElement = ({
  name,
  formatMessage,
  subscription,
  withTouched,
  notifyOnly = false,
  inField = false,
  children,
  className,
}) => {
  const [t] = useTranslation(['backend', 'common', 'components', 'messages']);
  const tFunction = React.useRef<TFunction>(t);
  React.useEffect(() => {
    tFunction.current = t;
  }, [t]);

  const { hasAnyError, error, isTouched, submitError } = useErrorSubscription({ name, subscription, withTouched });

  const displayError = error || submitError;
  // eslint-disable-next-line react-hooks/refs
  const format = formatMessage ?? tFunction.current;
  const formattedError = typeof displayError === 'string' ? format(displayError) : undefined;

  const debouncedAlert = useDebouncedCallback(() => {
    if (formattedError) notify.danger(format('ERROR'), formattedError);
  }, 200);

  React.useEffect(() => {
    if (notifyOnly && isTouched && displayError) {
      debouncedAlert();
    }
  }, [isTouched, error, submitError, notifyOnly, debouncedAlert, displayError]);

  return (
    <div id={`${name}-error`}>
      {!notifyOnly && formattedError && hasAnyError ? (
        <Wrapper className={className} $inField={inField}>
          {formattedError}
        </Wrapper>
      ) : (
        children && (
          <Wrapper className={className} $inField={inField}>
            {children}
          </Wrapper>
        )
      )}
    </div>
  );
};

export default React.memo(Error) as typeof Error;
